<template>
  <div class="belonging">
    <PublicHeader :username="username" :login-url="loginUrl" />

    <main>
      <section v-if="!city" class="sf-hero">
        <div class="sf-hero-copy">

          <p class="sf-eyebrow">FAITH FOR EVERYDAY LIFE</p>
          <h1>Find your rhythm.<br>Grow your <em>faith.</em></h1>
          <p class="sf-intro">
            Find a local church and make room for God in your everyday life—with
            Bible reading, prayer, and spiritual practices you can share in community.
          </p>

          <div class="sf-hero-actions">
            <a class="sf-primary" href="#discover">Find a Church →</a>
            <a class="sf-text-link" :href="appIntroUrl">Explore the App →</a>
          </div>

          <form class="sf-search" @submit.prevent="explore">
            <label for="sf-city">Find a church near you</label>
            <div class="sf-search-row">
              <select id="sf-city" v-model="city">
                <option value="">Choose a city</option>
                <option v-for="item in cities" :key="item" :value="item">
                  {{ item }}
                </option>
              </select>
              <button class="sf-primary" type="submit">Explore →</button>
            </div>
          </form>

          <button class="sf-help" @click="openGuide">
            Not sure where to start? Help me find a church →
          </button>
        </div>

        <div class="sf-hero-visual sf-clean-brand">
          <img class="sf-clean-brand-logo"
            src="/static/communities/storyfaith-icon-yellow.png"
            alt="StoryFaith">
        </div>
      </section>
    <section v-if="city" class="sf-city-hero">
      <div class="sf-city-hero-inner">
        <button type="button" class="sf-city-back" @click="backToCities">
          ← Explore all cities
        </button>
        <p class="sf-city-eyebrow">FIND YOUR COMMUNITY</p>
        <h1>{{ city.split(',')[0] }}</h1>
        <p class="sf-city-state">{{ city.split(',').slice(1).join(',').trim() }}</p>
        <p class="sf-city-count">
          {{ communities.filter(item => cityLabel(item) === city).length }}
          communities to explore
        </p>
        <label class="sf-city-switch">
          Explore another city
          <select v-model="city">
            <option v-for="label in cities" :key="label" :value="label">
              {{ label }}
            </option>
          </select>
        </label>
      </div>
    </section>


      <section v-if="guideOpen" ref="guide" class="sf-guide" aria-labelledby="sf-guide-title">
        <div>
          <p class="sf-eyebrow">A LITTLE DIRECTION</p>
          <h2 id="sf-guide-title">Let's find somewhere to begin.</h2>
          <p>Choose a starting point. You can always explore something different.</p>
        </div>
        <form @submit.prevent="applyGuide">
          <label for="sf-guide-city">Your city</label>
          <select id="sf-guide-city" v-model="city" required>
            <option value="">Choose a city</option>
            <option v-for="item in cities" :key="item" :value="item">{{ item }}</option>
          </select>
          <fieldset>
            <legend>What would help you take a first step?</legend>
            <label class="sf-choice">
              <input v-model="guideType" type="radio" value="Local Church">
              Find a local church
            </label>
            <label class="sf-choice">
              <input v-model="guideType" type="radio" value="College Ministry">
              Explore a college ministry
            </label>
            <label class="sf-choice">
              <input v-model="guideType" type="radio" value="">
              I'm open to exploring both
            </label>
          </fieldset>
          <button type="submit" class="sf-primary">Show me places to explore →</button>
          <button type="button" class="sf-help" @click="guideOpen = false">Close</button>
        </form>
      </section>


      <section v-if="!city && !isDiscovery" id="how-it-works" class="sf-path" aria-labelledby="sf-path-title">
        <p class="sf-eyebrow">BELONGING AND EVERYDAY FAITH</p>
        <h2 id="sf-path-title">A place to belong. A rhythm to grow.</h2>
        <div class="sf-path-grid">
          <article><span class="sf-step">01</span><h3>Find a place to belong</h3>
            <p>Explore local churches and Christian communities. Get to know their story before you take your first step through the door.</p>
            <a href="#discover">Find a Church →</a></article>
          <article><span class="sf-step">02</span><h3>Make room for God</h3>
            <p>Build a rhythm of Scripture, prayer, and solitude. Choose spiritual practices and a schedule that fits your everyday life.</p>
            <a href="#explore-app">Explore the App →</a></article>
          <article><span class="sf-step">03</span><h3>Grow together</h3>
            <p>Join practices from your community and reflect alongside your group. Choose what to share and what to keep private.</p>
            <a href="#explore-app">See how it comes together →</a></article>
        </div>
      </section>

      <section v-if="!city && !isDiscovery" id="explore-app" class="sf-app-intro" aria-labelledby="sf-app-title">
        <div class="sf-app-copy">
          <p class="sf-eyebrow">THE STORYFAITH APP</p>
          <h2 id="sf-app-title">Make room for faith<br>in your everyday life.</h2>
          <p>Start with Scripture, a moment of prayer, or a little stillness. Choose when you practice, return to your rhythm, and grow with others along the way.</p>
          <p>Your community can offer a shared Bible practice. You choose to join, make time for it, and share reflections with your group.</p>
          <a class="sf-text-link" href="mailto:hello@storyfaith.com?subject=StoryFaith%20app%20access">Ask about app access →</a>
        </div>
        <div class="sf-app-practices" aria-label="Practices in the StoryFaith app">
          <p class="sf-app-label">A LITTLE ROOM, EVERY DAY</p>
          <article><span aria-hidden="true">01</span><div><h3>Bible reading</h3><p>Read Scripture through a personal plan or a practice from your community.</p></div></article>
          <article><span aria-hidden="true">02</span><div><h3>Prayer</h3><p>Set aside time to pray and return to the prayers in your journal.</p></div></article>
          <article><span aria-hidden="true">03</span><div><h3>Solitude</h3><p>Make space for stillness with quiet time or gentle guidance.</p></div></article>
          <p class="sf-app-footnote">Your schedule. Your next step. Room to grow together.</p>
        </div>
      </section>

      <section v-if="!city" class="sf-cities" aria-labelledby="sf-cities-title">
        <p class="sf-eyebrow">FIND COMMUNITY CLOSE TO HOME</p>
        <h2 id="sf-cities-title">Explore our cities.</h2>
        <p>Discover local churches and ministries where you live.</p>
        <div class="sf-city-grid">
          <button v-for="item in cityCards" :key="item.label"
            class="sf-city-card" @click="chooseCity(item.label)">
            <span class="sf-city-pin" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="25" height="25" fill="none"
                stroke="currentColor" stroke-width="1.7">
                <path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z"/>
                <circle cx="12" cy="10" r="2.5"/>
              </svg>
            </span>
            <strong>{{ item.label }}</strong>
            <span>{{ item.count }} {{ item.count === 1 ? 'listed community' : 'listed communities' }}</span>
            <span class="sf-city-link">View communities →</span>
          </button>
        </div>
      </section>

      <section id="discover" ref="discover" class="sf-discover">
        <button v-if="city" class="sf-help sf-back-cities" @click="backToCities">
          ← Explore other cities
        </button>
        <div class="sf-section-heading">
          <div>
            <p class="sf-eyebrow">MAKE ROOM FOR BELONGING</p>
            <h2>{{ city ? 'Find your community in ' + city + '.' : 'Explore communities near you.' }}</h2>
            <p>Get a feel for a community before you walk through the door.</p>
          </div>
          <button v-if="city || kind || query || campus" class="sf-help" @click="reset">Reset filters</button>
        </div>

        <div class="sf-filter-panel">
        <h3>Find your community</h3>
        <div class="sf-filters">
          <div class="sf-tabs" role="group" aria-label="Community type">
            <button v-for="option in types" :key="option.value"
              :class="{ active: kind === option.value }"
              :aria-pressed="kind === option.value"
              @click="kind = option.value; shown = 12">{{ option.label }}</button>
          </div>
          <label class="sf-name-search">
            <span class="sf-sr-only">Search community names or locations</span>
            <input v-model="query" type="search" placeholder="Search by name or location">
          </label>
        </div>


        <button class="sf-more-filter" :aria-expanded="moreFilters"
          aria-controls="sf-extra-filters" @click="moreFilters = !moreFilters">
          {{ moreFilters ? 'Fewer filters −' : 'More filters +' }}
        </button>
        <div v-if="moreFilters" id="sf-extra-filters" class="sf-extra-filters">
          <label for="sf-campus">Campus listed by the community</label>
          <select id="sf-campus" v-model="campus">
            <option value="">All campuses and communities</option>
            <option v-for="item in campuses" :key="item" :value="item">{{ item }}</option>
          </select>
          <p>This filters campus information in the listings, rather than distance from campus.</p>
        </div>
        </div>

        <div v-if="saveMessage" class="sf-save-message" role="status">
  {{ saveMessage }}
  <a v-if="!username" :href="loginUrl">Sign in →</a>
</div>

<section v-if="username && savedInCity.length" class="sf-saved-section">
  <h3>{{ city ? 'Saved in this city' : 'Your saved communities' }}</h3>
  <p>Your shortlist. Saving does not register you as a member.</p>
  <div class="sf-saved-list">
    <article v-for="item in savedInCity" :key="item.slug">
      <a :href="'/' + item.slug + '/'">{{ item.title }} ↗</a>
      <button type="button"
        :disabled="savingSlugs.includes(item.slug)"
        @click="toggleSave(item)">
        {{ savingSlugs.includes(item.slug) ? 'Updating…' : 'Remove' }}
      </button>
    </article>
  </div>
</section>

<p class="sf-count" aria-live="polite">
          {{ filtered.length }} {{ filtered.length === 1 ? 'community' : 'communities' }}
          {{ city ? 'in ' + city : 'across our cities' }}
        </p>

        <div v-if="filtered.length" class="sf-grid">
        <article v-for="community in filtered.slice(0, shown)"
  :key="community.slug" class="sf-card">
            <div class="sf-card-image">
              <img v-if="community.cover" :src="community.cover" alt="" loading="lazy"
                @error="$event.target.style.visibility = 'hidden'">
              <span>{{ community.type || 'Community' }}</span>
            </div>
            <div class="sf-card-copy">
              <p class="sf-location">{{ community.city }}{{ community.state ? ', ' + community.state : '' }}</p>
              <h3>{{ community.title }}</h3>
              <p v-if="community.small_description">{{ community.small_description }}</p>
              <p v-if="community.university_name" class="sf-campus">{{ community.university_name }}</p>
              <div class="sf-discovery-card-actions">
  <a :href="'/' + community.slug + '/'">
    Get to know this community ↗
  </a>
  <button type="button"
    :aria-pressed="isSaved(community.slug)"
    :aria-label="(isSaved(community.slug) ? 'Remove saved ' : 'Save ') + community.title"
    :disabled="savingSlugs.includes(community.slug)"
    @click="toggleSave(community)">
    {{ savingSlugs.includes(community.slug)
      ? 'Updating…' : isSaved(community.slug) ? 'Saved ✓' : 'Save' }}
  </button>
</div>
            </div>
          </article>
        </div>

        <div v-else class="sf-empty">
          <h3>No communities match these filters yet.</h3>
          <p>Try another city, search, or community type.</p>
          <a class="sf-text-link" href="/interest/?intent=visitor&amp;source=discovery">Not finding a church near you? Stay in touch →</a>
          <button class="sf-primary" @click="reset">Explore all communities</button>
        </div>

        <button v-if="filtered.length > shown" class="sf-more" @click="shown += 12">
          Show more communities ↓
        </button>
      </section>

      <section v-if="!isDiscovery" class="sf-ending" aria-labelledby="sf-ending-title">
        <span class="sf-ending-accent" aria-hidden="true"></span>
        <h2 id="sf-ending-title">Your next step doesn't need to be complicated.</h2>
        <p>Discover a local church near you. Practice spiritual disciplines personalized to your schedule. Grow your faith in the context of community.</p>
        <div class="sf-ending-actions">
          <a class="sf-ending-primary" href="#discover">Find a Church →</a>
          <a class="sf-ending-secondary" :href="appIntroUrl">Explore the App →</a>
        </div>
      </section>
    </main>

    <PublicFooter />
  </div>
</template>

<script>
import PublicHeader from './PublicHeader.vue'
import PublicFooter from './PublicFooter.vue'
export default {
  components: { PublicHeader, PublicFooter },
  name: 'BelongingHome',
  data () {
    let communities = []
    try {
      communities = JSON.parse(document.getElementById('belonging-data').textContent)
    } catch (error) {
      console.error('Unable to load community listings', error)
    }
    let account = {}
try {
  const element = document.getElementById('discovery-account')
  if (element) account = JSON.parse(element.textContent)
} catch (error) {
  console.error('Unable to read discovery account', error)
}
const params = new URLSearchParams(window.location.search)
    return {
      communities,
      city: params.get('city') || '',
      kind: params.get('type') || '',
      query: params.get('q') || '',
      campus: params.get('campus') || '',
      guideOpen: false,
      guideType: 'Local Church',
      shown: 12,
      moreFilters: false,
      username: account.username || '',
      savedSlugs: account.saved_slugs || [],
      savingSlugs: [],
      saveMessage: '',
      types: [
        { value: '', label: 'All communities' },
        { value: 'Local Church', label: 'Local churches' },
        { value: 'College Ministry', label: 'College ministries' }
      ]
    }
  },
  computed: {
    isDiscovery () { return window.location.pathname === '/discover/' },
    appIntroUrl () { return this.isDiscovery || this.city ? '/?section=app#explore-app' : '#explore-app' },
    currentYear () { return new Date().getFullYear() },
  loginUrl () {
  return '/accounts/login/?next=' +
    encodeURIComponent(window.location.pathname + window.location.search)
},
savedInCity () {
  return this.communities.filter(item =>
    this.savedSlugs.includes(item.slug) &&
    (!this.city || this.cityLabel(item) === this.city)
  ).sort((a, b) => a.title.localeCompare(b.title, 'en', {
    sensitivity: 'base'
  }))
},
    cities () {
      return [...new Set(this.communities.map(this.cityLabel).filter(Boolean))].sort()
    },
    cityCards () {
      return this.cities.map(label => ({
        label,
        count: this.communities.filter(item => this.cityLabel(item) === label).length
      }))
    },
    campuses () {
      const items = this.communities.filter(item =>
        !this.city || this.cityLabel(item) === this.city
      )
      return [...new Set(items.map(item => item.university_name).filter(Boolean))].sort()
    },
    filtered () {
      const terms = this.query.trim().toLowerCase().split(/[\s,]+/).filter(Boolean)
      return this.communities.filter(item =>
        (!this.city || this.cityLabel(item) === this.city) &&
        (!this.kind || item.type === this.kind) &&
        (!this.campus || item.university_name === this.campus) &&
        terms.every(term => [item.title, item.city, item.state].some(value =>
          (value || '').toLowerCase().includes(term)))
      ).sort((a, b) => a.title.localeCompare(b.title, 'en', { sensitivity: 'base' }))
    },
    heroImage () {
      const photos = this.communities.flatMap(item => item.photos || [])
      return photos[0] || ''
    }
  },
  watch: {
    city () {
      this.shown = 12
      this.campus = ''
      this.rememberFilters()
    },
    kind () {
      this.shown = 12
      this.rememberFilters()
    },
    campus () {
      this.shown = 12
      this.rememberFilters()
    },
    query () {
      this.shown = 12
      this.rememberFilters()
    }
  },

  methods: {
  rememberFilters () {
  // The public homepage keeps its existing behavior.
  if (window.location.pathname !== '/discover/') return
  const url = new URL(window.location.href)
  for (const [key, value] of Object.entries({
    city: this.city, type: this.kind, q: this.query, campus: this.campus
  })) {
    if (value) url.searchParams.set(key, value)
    else url.searchParams.delete(key)
  }
  window.history.replaceState(null, '', url.pathname + url.search + url.hash)
},
isSaved (slug) {
  return this.savedSlugs.includes(slug)
},
updateSaved (slug, saved) {
  this.savedSlugs = this.savedSlugs.filter(value => value !== slug)
  if (saved) this.savedSlugs.push(slug)
},
csrfToken () {
  const cookie = document.cookie.split(';')
    .map(value => value.trim())
    .find(value => value.startsWith('csrftoken='))
  return cookie ? decodeURIComponent(cookie.slice('csrftoken='.length)) : ''
},
async readSavedState (slug) {
  const response = await fetch(
    '/api/account/' + encodeURIComponent(this.username) +
    '/favoritecommunity/?community=' + encodeURIComponent(slug),
    { credentials: 'same-origin', cache: 'no-store' }
  )
  if (!response.ok) throw new Error('Unable to check saved state')
  const value = await response.json()
  if (typeof value !== 'boolean') throw new Error('Unexpected saved state')
  return value
},
async toggleSave (community) {
  if (!this.username) {
    this.saveMessage = 'Sign in to save this community. You’ll return to your discovery filters.'
    return
  }
  if (this.savingSlugs.includes(community.slug)) return
  const desired = !this.isSaved(community.slug)
  this.savingSlugs.push(community.slug)
  this.saveMessage = ''
  try {
    const current = await this.readSavedState(community.slug)
    if (current !== desired) {
      const body = new FormData()
      body.append('community', community.slug)
      const response = await fetch(
        '/api/account/' + encodeURIComponent(this.username) + '/favoritecommunity/',
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-CSRFToken': this.csrfToken() },
          body
        }
      )
      if (!response.ok) throw new Error('Save request failed')
    }
    const confirmed = await this.readSavedState(community.slug)
    this.updateSaved(community.slug, confirmed)
    this.saveMessage = confirmed === desired
      ? community.title + (confirmed ? ' saved.' : ' removed from saved communities.')
      : 'The saved state changed elsewhere. Please check this community again.'
  } catch (error) {
    this.saveMessage = 'We couldn’t confirm the change. Refresh to check your saved communities before trying again.'
  } finally {
    this.savingSlugs = this.savingSlugs.filter(slug => slug !== community.slug)
  }
},
    chooseCity (label) {
      this.city = label
      this.kind = ''
      this.query = ''
      this.$nextTick(() => this.explore())
    },
    backToCities () {
      this.reset()
      this.$nextTick(() => {
        document.getElementById('sf-cities-title').scrollIntoView({
          behavior: 'smooth', block: 'center'
        })
      })
    },
    cityLabel (item) {
      return item.city ? item.city + (item.state ? ', ' + item.state : '') : ''
    },
    explore () {
      this.$refs.discover.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    openGuide () {
      this.guideOpen = true
      this.$nextTick(() => {
        this.$refs.guide.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.$refs.guide.querySelector('select').focus({ preventScroll: true })
      })
    },
    applyGuide () {
      this.kind = this.guideType
      this.explore()
    },
    reset () {
      this.city = ''
      this.kind = ''
      this.query = ''
      this.campus = ''
      this.shown = 12
    }
  }
}
</script>

<style>
#sf-belonging > header,
#sf-belonging > footer,
#sf-belonging > .main-content > .container { display: none; }
#sf-belonging { margin: 0; background: #faf8f2; }
#sf-belonging .main-content { margin: 0; padding: 0; }
.belonging { --ink: #222222; --muted: #62695f; --gold: #ffd400; color: var(--ink); background: #faf8f2; font-family: "Inter", "Open Sans", Arial, sans-serif; }
.belonging *, .belonging *::before, .belonging *::after { box-sizing: border-box; }
.belonging h1, .belonging h2, .belonging h3, .belonging p { margin: 0; text-align: left; }
.belonging a { color: inherit; text-decoration: none; }
.belonging button, .belonging input, .belonging select { font: inherit; }
.belonging button { cursor: pointer; }
.belonging button:focus-visible, .belonging a:focus-visible,
.belonging input:focus-visible, .belonging select:focus-visible { outline: 3px solid #557bba; outline-offset: 4px; }
.sf-nav, .sf-footer { max-width: 1240px; margin: auto; padding: 28px 40px; display: flex; align-items: center; gap: 28px; }
.sf-brand { font-size: 27px; font-weight: 800; letter-spacing: -1.5px; }
.sf-brand span { color: #af8136; }
.sf-nav nav { margin-left: auto; display: flex; gap: 30px; font-size: 14px; }
.sf-hero { max-width: 1240px; margin: auto; padding: 76px 40px 92px; display: grid; grid-template-columns: 1.1fr 1fr; gap: 70px; align-items: center; }
.sf-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; color: #776944; margin-bottom: 22px !important; }
.belonging h1 { font-family: Georgia, serif; font-size: clamp(48px, 5.5vw, 78px); font-weight: 400; line-height: 1.06; letter-spacing: -3px; }
.belonging h1 em { color: #7b8659; font-weight: 400; }
.sf-intro { color: var(--muted); max-width: 480px; line-height: 1.85; margin-top: 25px !important; font-size: 15px; }
.sf-search { background: white; border: 1px solid #e6e3d9; border-radius: 15px; padding: 18px; margin: 30px 0 16px; box-shadow: 0 8px 30px #293c2f08; }
.sf-search label, .sf-guide form > label { display: block; font-size: 12px; font-weight: 700; margin-bottom: 10px; }
.sf-search-row { display: flex; gap: 10px; }
.belonging select, .sf-name-search input { border: 1px solid #dddcd3; background: #fff; padding: 12px; border-radius: 8px; color: var(--ink); min-width: 0; }
.sf-search select { flex: 1; width: 100%; }
.sf-primary { border: 0; border-radius: 8px; background: var(--ink); color: white; padding: 13px 20px; font-size: 13px !important; font-weight: 600; }
.sf-primary:hover { background: #435847; }
.sf-help { border: 0; background: none; color: #66532f; padding: 8px 0; font-size: 12px !important; text-align: left; }
.sf-hero-visual { position: relative; padding-bottom: 30px; }
.sf-photo { height: 440px; background: linear-gradient(150deg, #869b74, #314c3e); background-size: cover; background-position: center; border-radius: 120px 16px 16px 16px; position: relative; overflow: hidden; }
.sf-photo::after { content: ""; position: absolute; inset: 0; background: linear-gradient(transparent 45%, #102719bb); }
.sf-photo-caption { position: absolute; bottom: 35px; left: 30px; z-index: 1; color: white; font-family: Georgia, serif; font-size: 27px; }
.sf-photo-caption span, .sf-photo-caption strong { display: block; font-weight: 400; }
.sf-note { position: relative; margin: -30px 20px 0 -25px; z-index: 2; display: flex; gap: 15px; padding: 20px; background: #efe7d4; border-radius: 12px; box-shadow: 0 10px 25px #26362e10; }
.sf-note-icon { font-size: 27px; }
.sf-note strong { font-size: 13px; }
.sf-note p { font-size: 11px; margin-top: 5px; line-height: 1.7; }
.sf-guide { max-width: 1160px; margin: 0 auto 60px; background: #edf2f8; border: 1px solid #d9e2ef; padding: 40px; border-radius: 18px; display: grid; grid-template-columns: 1fr 1fr; gap: 50px; scroll-margin-top: 24px; }
.belonging h2 { font-family: Georgia, serif; font-weight: 400; font-size: 37px; line-height: 1.15; letter-spacing: -1px; }
.sf-guide p:not(.sf-eyebrow), .sf-section-heading p:not(.sf-eyebrow) { color: var(--muted); font-size: 14px; line-height: 1.8; margin-top: 16px; }
.sf-guide select { width: 100%; }
.sf-guide fieldset { border: 0; padding: 0; margin: 22px 0; }
.sf-guide legend { font-size: 13px; font-weight: 700; margin-bottom: 12px; }
.sf-choice { display: flex; align-items: center; gap: 10px; padding: 9px 0; font-size: 13px; }
.sf-guide .sf-help { margin-left: 15px; }
.sf-discover { max-width: 1240px; margin: auto; padding: 65px 40px 85px; border-top: 1px solid #e6e2d6; scroll-margin-top: 24px; }
.sf-section-heading { display: flex; justify-content: space-between; align-items: start; gap: 20px; }
.sf-filters { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin: 30px 0 18px; flex-wrap: wrap; }
.sf-tabs { display: flex; flex-wrap: wrap; gap: 8px; }
.sf-tabs button { border: 1px solid #dedfd5; background: transparent; color: var(--ink); border-radius: 30px; padding: 10px 17px; font-size: 12px; }
.sf-tabs button.active { background: var(--ink); color: white; border-color: var(--ink); }
.sf-name-search input { font-size: 12px; }
.sf-count { font-size: 11px; color: var(--muted); margin-bottom: 22px !important; }
.sf-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 25px; }
.sf-card { background: #fff; border: 1px solid #e6e4dc; border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; transition: transform .18s, box-shadow .18s; }
.sf-card:hover { transform: translateY(-4px); box-shadow: 0 12px 30px #26362e12; }
.sf-card-image { height: 210px; background: #e7e9de; position: relative; }
.sf-card-image img { width: 100%; height: 100%; object-fit: cover; }
.sf-card-image > span { position: absolute; left: 15px; bottom: 15px; background: #fffef8; border-radius: 30px; padding: 6px 12px; font-size: 10px; }
.sf-card-copy { padding: 23px; display: flex; flex-direction: column; flex: 1; }
.sf-location { color: var(--muted); font-size: 10px; margin-bottom: 9px !important; }
.belonging h3 { font-size: 20px; font-weight: 600; letter-spacing: -.5px; }
.sf-card-copy > p:not(.sf-location) { font-size: 12px; line-height: 1.8; color: var(--muted); margin-top: 10px; }
.sf-card-copy strong { font-size: 11px; font-weight: 600; padding-top: 23px; margin-top: auto; display: flex; justify-content: space-between; gap: 8px; }
.sf-campus { color: #776944 !important; }
.sf-more { display: block; margin: 35px auto 0; border: 1px solid #d1d5c7; border-radius: 8px; padding: 12px 22px; background: transparent; font-size: 12px !important; }
.sf-empty { padding: 45px; border: 1px solid #dedfd5; border-radius: 12px; }
.sf-empty p { margin: 15px 0 25px; color: var(--muted); }
.sf-ending { background: #eaeedc; padding: 75px 24px; text-align: center; }
.sf-ending h2, .sf-ending p { text-align: center; }
.sf-ending > p:not(.sf-eyebrow) { margin: 20px auto 25px; font-size: 13px; color: var(--muted); }
.sf-footer { flex-wrap: wrap; padding-top: 40px; padding-bottom: 40px; }
.sf-footer p { flex: 1; font-size: 11px; color: var(--muted); }
.sf-footer > a:not(.sf-brand) { font-size: 11px; }
.sf-sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
@media (max-width: 900px) {
  .sf-hero { gap: 30px; }
  .sf-photo { height: 380px; }
  .sf-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 650px) {
  .sf-nav, .sf-footer { padding: 22px; gap: 16px; }
  .sf-nav nav { gap: 16px; font-size: 11px; }
  .sf-brand { font-size: 23px; }
  .sf-hero { padding: 45px 22px 55px; grid-template-columns: 1fr; gap: 35px; }
  .belonging h1 { letter-spacing: -2px; }
  .sf-photo { height: 310px; border-top-left-radius: 80px; }
  .sf-note { margin-left: 0; }
  .sf-guide { margin: 0 22px 30px; padding: 25px; grid-template-columns: 1fr; gap: 25px; }
  .sf-discover { padding: 40px 22px; }
  .belonging h2 { font-size: 30px; }
  .sf-grid { grid-template-columns: 1fr; }
  .sf-card-image { height: 235px; }
  .sf-section-heading { display: block; }
  .sf-footer p { flex-basis: 100%; }
}
@media (prefers-reduced-motion: reduce) {
  .sf-card { transition: none; }
}

/* Keep primary actions readable alongside legacy styles. */
#sf-belonging .belonging button.sf-primary {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
  font-size: 13px !important;
  line-height: 1.5 !important;
  text-indent: 0 !important;
  opacity: 1;
}


.sf-cities { max-width: 1240px; margin: auto; padding: 55px 40px 75px; }
.sf-cities > p:not(.sf-eyebrow) { margin-top: 16px; color: #62695f; font-size: 14px; }
.sf-city-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; margin-top: 32px; }
.sf-city-card { display: flex; flex-direction: column; align-items: center; gap: 15px; padding: 32px 20px; background: white; border: 1px solid #e6e4dc; border-radius: 18px; color: #26362e; transition: border-color .18s, box-shadow .18s; }
.sf-city-card:hover { border-color: #26362e; box-shadow: 0 8px 25px #26362e0c; }
.sf-city-pin { display: grid; place-items: center; width: 58px; height: 58px; background: #faf8f2; border: 1px solid #e6e4dc; border-radius: 50%; }
.sf-city-card strong { font-size: 20px; letter-spacing: -.4px; }
.sf-city-card > span:not(.sf-city-pin) { font-size: 12px; color: #62695f; }
.sf-city-card > .sf-city-link { color: #26362e !important; font-weight: 600; margin-top: 7px; }
.sf-back-cities { margin-bottom: 25px; }
.sf-filter-panel { padding: 25px; margin: 30px 0 25px; border: 1px solid #e6e4dc; border-radius: 16px; background: white; box-shadow: 0 5px 18px #26362e05; }
.sf-filter-panel > h3 { font-size: 17px; }
.sf-filter-panel .sf-filters { margin: 20px 0 15px; }
.sf-more-filter { border: 1px solid #dedfd5; background: white; border-radius: 30px; color: #26362e; padding: 10px 17px; font-size: 12px !important; }
.sf-extra-filters { border-top: 1px solid #e6e4dc; margin-top: 20px; padding-top: 20px; }
.sf-extra-filters label { display: block; font-size: 12px; font-weight: 600; margin-bottom: 10px; }
.sf-extra-filters select { width: 100%; max-width: 400px; }
.sf-extra-filters p { font-size: 11px; color: #62695f; margin-top: 10px; }
@media (max-width: 900px) { .sf-city-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 650px) {
  .sf-cities { padding: 35px 22px; }
  .sf-city-grid { grid-template-columns: 1fr; gap: 15px; }
  .sf-city-card { padding: 25px; }
  .sf-filter-panel { padding: 20px; }
}
@media (prefers-reduced-motion: reduce) { .sf-city-card { transition: none; } }


/* Shared city placeholder; ready for city photography later. */
#sf-belonging .sf-city-hero {
  position: relative;
  padding: 56px 28px 64px;
  background:
    radial-gradient(ellipse at 85% 15%, #cbd6c1 0%, transparent 55%),
    linear-gradient(135deg, #e8e9dd, #f3eee2 65%, #faf8f2);
}
#sf-belonging .sf-city-hero-inner {
  max-width: 1180px;
  margin: 0 auto;
}
#sf-belonging .sf-city-back {
  border: 0;
  background: transparent;
  padding: 0;
  color: #26362e;
  font-size: 13px;
  cursor: pointer;
}
#sf-belonging .sf-city-eyebrow {
  margin: 36px 0 12px;
  font-size: 11px;
  letter-spacing: .16em;
  color: #62695f;
}
#sf-belonging .sf-city-hero h1 {
  margin: 0;
  font-family: Georgia, serif;
  font-size: clamp(42px, 7vw, 76px);
  line-height: 1.1;
  color: #26362e;
}
#sf-belonging .sf-city-state {
  margin: 12px 0;
  font-size: 18px;
  color: #62695f;
}
#sf-belonging .sf-city-count {
  margin: 20px 0;
  color: #26362e;
}
#sf-belonging .sf-city-switch {
  display: block;
  max-width: 320px;
  font-size: 12px;
  color: #62695f;
}
#sf-belonging .sf-city-switch select {
  display: block;
  width: 100%;
  margin-top: 8px;
  padding: 12px;
  border: 1px solid #d6d9cb;
  border-radius: 8px;
  background: #fff;
  color: #26362e;
}


/* Original StoryFaith brand */
#sf-belonging .belonging .sf-brand .sf-original-logo {
  display: block;
  width: 145px;
  height: auto;
}


/* StoryFaith charcoal framing */
#sf-belonging .belonging .sf-nav,
#sf-belonging .belonging .sf-footer {
  background: #222222 !important;
  color: #faf8f2 !important;
  border-color: #333333 !important;
}

#sf-belonging .belonging .sf-nav a,
#sf-belonging .belonging .sf-footer a,
#sf-belonging .belonging .sf-footer p {
  color: #faf8f2 !important;
}
.sf-save-message {
  padding: 16px; margin: 20px 0;
  background: #eaeedc; border-radius: 10px; font-size: 13px;
}
.sf-save-message a { margin-left: 10px; text-decoration: underline; }
.sf-saved-section {
  padding: 24px; margin: 24px 0; background: #fff;
  border: 1px solid #d7dacd; border-radius: 14px;
}
.sf-saved-section h3 { text-align: left; }
.sf-saved-section p { font-size: 13px; color: #62695f; }
.sf-saved-list { display: flex; flex-wrap: wrap; gap: 12px; }
.sf-saved-list article {
  display: flex; align-items: center; gap: 16px;
  padding: 12px 14px; background: #faf8f2; border-radius: 9px;
}
.sf-saved-list button, .sf-discovery-card-actions button {
  padding: 8px 12px; border: 1px solid #d7dacd;
  border-radius: 8px; background: #fff; color: #26362e; font-size: 12px;
}
.sf-discovery-card-actions {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 12px; margin-top: 18px;
}
.sf-discovery-card-actions a { font-size: 12px; font-weight: 700; }
.sf-discovery-card-actions button[aria-pressed="true"] { background: #eaeedc; }
.sf-discovery-card-actions button:disabled { opacity: .6; }

/* Homepage invitation and app introduction. */
.sf-hero-actions, .sf-ending-actions { display:flex; align-items:center; flex-wrap:wrap; gap:24px; }
.sf-hero-actions { margin-top:28px; }
.belonging .sf-text-link { font-size:14px; font-weight:600; text-decoration:underline; text-underline-offset:5px; }
.sf-primary { display:inline-flex; align-items:center; justify-content:center; min-height:44px; }
.belonging .sf-primary:hover { background:#3b3b3b; color:#fff; }
.sf-path { max-width:1240px; margin:auto; padding:36px 40px 70px; }
.sf-path-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:24px; margin-top:32px; }
.sf-path-grid article { padding:28px; border:1px solid #e6e3d9; border-radius:18px; background:#fff; }
.sf-step { display:inline-block; border-bottom:3px solid #ffd400; padding-bottom:8px; margin-bottom:22px; font-size:12px; }
.belonging .sf-path h3 { font-family:Georgia,serif; font-size:25px; font-weight:400; line-height:1.25; }
.sf-path-grid p { margin-top:16px; color:var(--muted); font-size:14px; line-height:1.85; }
.sf-path-grid a { display:inline-block; margin-top:24px; font-size:13px; font-weight:600; padding:8px 0; }
.sf-app-intro { max-width:1160px; margin:0 auto 55px; padding:44px; display:grid; grid-template-columns:1fr 1fr; gap:56px; background:#eeeee5; border-radius:24px; scroll-margin-top:24px; }
.sf-app-copy > p:not(.sf-eyebrow) { margin-top:22px; font-size:14px; line-height:1.85; color:var(--muted); }
.sf-app-copy > a { display:inline-block; margin-top:22px; padding:10px 0; }
.sf-app-practices { padding:28px; background:#fff; border:1px solid #e6e3d9; border-radius:20px; }
.sf-app-label { font-size:10px; letter-spacing:1.8px; color:var(--muted); }
.sf-app-practices article { display:flex; gap:18px; padding:23px 0; border-bottom:1px solid #e6e3d9; }
.sf-app-practices article > span { font-size:11px; color:var(--muted); padding-top:5px; }
.belonging .sf-app-practices h3 { font-family:Georgia,serif; font-size:24px; font-weight:400; }
.sf-app-practices article p { font-size:13px; line-height:1.8; color:var(--muted); margin-top:9px; }
.sf-app-footnote { margin-top:20px !important; font-size:12px; line-height:1.8; color:var(--muted); }
#sf-belonging .belonging .sf-ending { background:#222; color:#faf8f2; padding:76px 24px; }
.sf-ending-accent { display:block; width:42px; height:3px; background:#ffd400; margin:0 auto 28px; }
.belonging .sf-ending h2 { font-size:clamp(32px,4vw,52px); }
#sf-belonging .sf-ending > p { color:#dedcd5; max-width:660px; font-size:16px; line-height:1.85; margin:22px auto 30px; }
.sf-ending-actions { justify-content:center; }
.belonging .sf-ending-primary { background:#faf8f2; color:#222; border-radius:10px; padding:16px 24px; font-size:14px; font-weight:600; min-height:48px; }
.belonging .sf-ending-secondary { color:#faf8f2; padding:16px 8px; font-size:14px; text-decoration:underline; text-underline-offset:6px; }
#sf-belonging .belonging .sf-footer { display:block; max-width:none; padding:52px max(24px,calc((100% - 1160px)/2)); border-top:1px solid #444; }
.sf-footer-top { display:grid; grid-template-columns:1fr 3fr; gap:50px; }
.sf-footer-brand > p { margin-top:24px; line-height:1.8; font-size:14px; }
.sf-footer-links { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:24px; }
.belonging .sf-footer-links h2 { font-family:Inter,"Open Sans",Arial,sans-serif; font-size:13px; font-weight:600; letter-spacing:0; margin-bottom:16px; }
.sf-footer-links a { display:block; padding:8px 0; font-size:12px; line-height:1.6; }
.sf-footer-links a:hover { text-decoration:underline; text-underline-offset:4px; }
.sf-footer-bottom { border-top:1px solid #444; margin-top:36px; padding-top:24px; display:flex; justify-content:space-between; flex-wrap:wrap; gap:16px; }
.sf-footer-bottom p { font-size:11px; line-height:1.8; }
@media(max-width:900px) { .sf-app-intro { margin:0 24px 40px; gap:30px; padding:30px; } .sf-path-grid { gap:15px; } .sf-path-grid article { padding:22px; } .sf-footer-top { grid-template-columns:1fr; gap:32px; } }
@media(max-width:650px) { .sf-nav { flex-wrap:wrap; } .sf-nav nav { flex-wrap:wrap; gap:14px; } .sf-path { padding:25px 22px 45px; } .sf-path-grid,.sf-app-intro { grid-template-columns:1fr; } .sf-app-intro { padding:24px; margin:0 22px 35px; } .sf-app-practices { padding:22px; } .sf-footer-links { grid-template-columns:repeat(2,minmax(0,1fr)); gap:28px; } #sf-belonging .belonging .sf-ending { padding:54px 22px; } .sf-ending-actions { gap:12px; } .sf-footer-bottom { display:block; } .sf-footer-bottom p + p { margin-top:12px; } }


/* StoryFaith CTA text contrast */
#sf-belonging .belonging a.sf-primary,
#sf-belonging .belonging a.sf-primary:visited,
#sf-belonging .belonging a.sf-primary:hover,
#sf-belonging .belonging a.sf-primary:focus {
  color: #ffffff !important;
  -webkit-text-fill-color: #ffffff;
}


/* Original StoryFaith logo in the homepage hero. */
#sf-belonging .sf-logo-hero {
  background: #222222;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px 130px;
}
#sf-belonging .sf-logo-hero::after {
  display: none;
}
#sf-belonging .sf-hero-logo {
  display: block;
  width: 70%;
  max-width: 280px;
  max-height: 220px;
  height: auto;
  object-fit: contain;
}


/* StoryFaith yellow and charcoal app introduction. */
#sf-belonging .belonging .sf-hero h1 em { color: #ffd400; }
#sf-belonging .belonging .sf-app-intro { background: #222222; color: #faf8f2; }
#sf-belonging .belonging .sf-app-copy h2 { color: #faf8f2; }
#sf-belonging .belonging .sf-app-copy > p:not(.sf-eyebrow) { color: #dedcd5; }
#sf-belonging .belonging .sf-app-copy .sf-eyebrow { color: #ffd400; }
#sf-belonging .belonging .sf-app-copy > a { color: #faf8f2; }
/* Keep reading examples calm and legible on white cards. */
#sf-belonging .belonging .sf-app-practices { color: #222222; }
#sf-belonging .belonging .sf-app-practices h3 { color: #222222; }

/* Original yellow StoryFaith mark, without a decorative panel. */
#sf-belonging .belonging .sf-clean-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: transparent;
}
#sf-belonging .belonging .sf-clean-brand-logo {
  display: block;
  width: 100%;
  max-width: 460px;
  height: auto;
  object-fit: contain;
}
@media (max-width: 650px) {
  #sf-belonging .belonging .sf-clean-brand-logo {
    max-width: 300px;
  }
}

</style>
